/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { ComponentRendererProps } from '../ComponentRenderer';
import { Bubble } from '../Bubble';
import { PlatformTextRenderer } from './Text';
import { PlatformLocaleTextRenderer } from './LocaleText';
import { PlatformAudioRenderer } from './Audio';
import { PlatformImageRenderer } from './Image';
import { PlatformVideoRenderer } from './Video';
import { Flex, Image } from '@chakra-ui/react';
import { FixedEnumInputParam } from '@karya/core/src/Index';

import { useEffect, type JSX } from 'react';

export const defaultMessageDisplay: FixedEnumInputParam = {
  src: 'CONSTANT',
  type: 'ENUM',
  value: 'DEFAULT',
  options: {
    INSTRUCTION: 'INSTRUCTION',
    EXAMPLE: 'EXAMPLE',
    TUTORIAL: 'TUTORIAL',
    REFERENCE: 'REFERENCE',
    DEFAULT: 'DEFAULT',
  },
};

export function PlatformComponentRenderer(props: ComponentRendererProps) {
  useEffect(() => {
    if (props.isCurrent) {
      props.next();
    }
  }, [props, props.isCurrent, props.next]);

  if (props.node.component.ctype != 'PLATFORM' || props.isDocked) {
    return null;
  }

  const details = props.node.component;
  const hasMessageDisplay = details && 'messageDisplay' in details;
  const messageDisplay = hasMessageDisplay
    ? (details as { messageDisplay: FixedEnumInputParam }).messageDisplay
    : defaultMessageDisplay;

  let component: JSX.Element;
  switch (props.node.component.dtype) {
    case 'TEXT':
      component = <PlatformTextRenderer params={props.node.params} />;
      break;
    case 'LOCALE_TEXT':
      component = <PlatformLocaleTextRenderer params={props.node.params} />;
      break;
    case 'VIDEO':
      component = <PlatformVideoRenderer params={props.node.params} />;
      break;
    case 'IMAGE':
      component = <PlatformImageRenderer params={props.node.params} />;
      break;
    case 'AUDIO':
      component = <PlatformAudioRenderer params={props.node.params} />;
      break;
    case 'IMAGE_MASK':
      component = <PlatformImageRenderer params={props.node.params} />;
      break;
  }

  return (
    <Flex gap={2} alignItems="flex-start">
      {hasMessageDisplay && messageDisplay.value !== 'DEFAULT' && <Image src={'/avatar_pic.svg'} />}
      <Bubble type="PLATFORM" messageDisplay={messageDisplay}>
        {component}
      </Bubble>
    </Flex>
  );
}
